import { useRef, useState } from 'react';
import { getMessageWithPlaceholders } from 'shared-locales';
import {
  getCalendarEventFeedbackWindowState,
  validateCalendarEventFeedbackAnswers
} from 'shared-helpers/calendar';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import {
  buildCalendarEventFeedbackAnswer,
  buildCalendarEventFeedbackFormValues
} from 'lib/calendar';
import { getFormattedDate } from 'lib/common';
import {
  Alert,
  ConfirmButton,
  EmptyIndicator,
  RichTextValue
} from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import { Form } from 'components/common/form';
import {
  CalendarEventFeedbackSubmissionDelete,
  CalendarEventFeedbackSubmissionQuestion
} from '.';

import type { FormApi } from 'informed';
import type { GetCalendarEventFeedbackSubmissionRes } from 'shared-types/responses';

type FormValues = Record<string, unknown>;

/**
 * Builds the answers the server expects from the form values. Questions left
 * unanswered are dropped, so the server sees them as not answered at all.
 */
const buildAnswers = (
  values: FormValues,
  questions: GetCalendarEventFeedbackSubmissionRes['questions']
) =>
  questions
    .map((question) => buildCalendarEventFeedbackAnswer({ question, values }))
    .filter(Boolean);

/**
 * Whether the form can be submitted. The answers are built first and then run
 * through the very same validator the API uses, so the button can never
 * disagree with what the server will accept.
 */
const isFormValid = (
  values: FormValues,
  questions: GetCalendarEventFeedbackSubmissionRes['questions']
) =>
  !validateCalendarEventFeedbackAnswers({
    answers: buildAnswers(values, questions),
    questions
  });

const CalendarEventFeedbackSubmission = () => {
  const { dateNow: now } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { calendarEvent },
    theme,
    user
  } = useReduxState();

  const { data, loading, updateData } =
    useApiData<GetCalendarEventFeedbackSubmissionRes>({
      url: `/admin/calendar-events/${calendarEvent.id}/feedback-form/submit`
    });

  const formApi = useRef<FormApi<FormValues>>(null);

  const userIsOwnerOrSuperadmin =
    calendarEvent.userIsOwner || user?.roles.includes('superadmin');

  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);

  // both dates are shown to the user as well, and the fallback to the event's
  // end date is the same one the window helper applies
  const startDate =
    calendarEvent.feedbackAccessStartDate || calendarEvent.endDate;
  const endDate = calendarEvent.feedbackAccessEndDate;

  const windowState = getCalendarEventFeedbackWindowState({
    event: calendarEvent,
    now
  });

  const showForm = userIsOwnerOrSuperadmin || windowState !== 'not_open_yet';

  const disableForm =
    data?.isSubmitted || (!userIsOwnerOrSuperadmin && windowState !== 'open');

  const handleSubmit = async (values: FormValues) => {
    try {
      await api.post(
        `/admin/calendar-events/${calendarEvent.id}/feedback-form/submit`,
        { answers: buildAnswers(values, data?.questions || []) }
      );

      await updateData();
      setHasTriedToSubmit(false);

      dispatch.addNotice({
        text: messages.eventCalendarFeedbackSubmitSuccess,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const handleConfirmSubmit = () => {
    formApi.current?.submitForm();
    return true;
  };

  const handleValidateBeforeClick = () => {
    const isValid = isFormValid(
      formApi.current?.getState().values || {},
      data?.questions || []
    );
    if (!isValid) setHasTriedToSubmit(true);

    return isValid;
  };

  return (
    <div className="submission">
      <Box
        loading={loading}
        title={
          userIsOwnerOrSuperadmin ? (
            <BoxBackLink
              as={`/calendar/${calendarEvent.id}/feedback`}
              href="/calendar/[calendarEventId]/feedback"
              text={messages.commonFeedback}
            />
          ) : (
            messages.commonFeedback
          )
        }
      >
        {!calendarEvent.feedbackEnabled ? (
          <Alert
            style={{ marginBottom: 20 }}
            text={messages.eventCalendarFeedbackNotEnabled}
            type="error-light"
          />
        ) : windowState === 'not_open_yet' && userIsOwnerOrSuperadmin ? (
          <Alert
            style={{ marginBottom: 20 }}
            text={getMessageWithPlaceholders({
              message: messages.eventCalendarFeedbackNotOpenYet,
              placeholders: {
                date: getFormattedDate({
                  date: startDate,
                  dateFormat: 'dd.MM.y HH:mm'
                })
              }
            })}
            type="warning-light"
          />
        ) : windowState === 'not_open_yet' ? (
          <EmptyIndicator
            icon="bullhorn"
            title={getMessageWithPlaceholders({
              message: messages.eventCalendarFeedbackNotOpenYet,
              placeholders: {
                date: getFormattedDate({
                  date: startDate,
                  dateFormat: 'dd.MM.y HH:mm'
                })
              }
            })}
          />
        ) : windowState === 'closed' ? (
          <Alert
            style={{ marginBottom: 20 }}
            text={messages.eventCalendarFeedbackClosed}
            type="warning-light"
          />
        ) : endDate ? (
          <Alert
            style={{ marginBottom: 20 }}
            text={getMessageWithPlaceholders({
              message: messages.eventCalendarFeedbackOpenUntil,
              placeholders: {
                date: getFormattedDate({
                  date: endDate,
                  dateFormat: 'dd.MM.y HH:mm'
                })
              }
            })}
            type="warning-light"
          />
        ) : null}

        {showForm ? (
          <>
            {calendarEvent.feedbackInfoText ? (
              <div className="info-text">
                <RichTextValue html={calendarEvent.feedbackInfoText} />
              </div>
            ) : null}

            <div className="content">
              {!loading && !data?.questions?.length ? (
                <EmptyIndicator
                  icon="question-circle"
                  title={messages.eventCalendarFeedbackQuestionsEmpty}
                />
              ) : data ? (
                <Form<FormValues>
                  getApi={(form) => (formApi.current = form)}
                  initialValues={buildCalendarEventFeedbackFormValues({
                    answers: data.answers,
                    questions: data.questions
                  })}
                  // remounted when the submission is deleted so the fields go back
                  // to being empty instead of keeping the deleted answers
                  key={data.isSubmitted ? 'submitted' : 'unanswered'}
                  onSubmit={handleSubmit}
                >
                  {() => (
                    <>
                      <div className="questions">
                        {data.questions.map((question) => (
                          <CalendarEventFeedbackSubmissionQuestion
                            disabled={disableForm}
                            key={question.id}
                            question={question}
                            showError={hasTriedToSubmit}
                          />
                        ))}
                      </div>

                      <div className="footer">
                        {data.isSubmitted ? (
                          userIsOwnerOrSuperadmin ? (
                            <div className="delete">
                              <CalendarEventFeedbackSubmissionDelete
                                submissionId={data.submissionId}
                                updateSubmission={updateData}
                              />
                            </div>
                          ) : null
                        ) : (
                          <div className="submit">
                            <ConfirmButton
                              actionCategory="success"
                              actionText={messages.commonSubmit}
                              buttonCategory="success"
                              buttonText={messages.eventCalendarFeedbackSubmit}
                              confirmText={
                                messages.eventCalendarFeedbackSubmitConfirmMessage
                              }
                              confirmTitle={
                                messages.eventCalendarFeedbackSubmit
                              }
                              disabled={disableForm}
                              onConfirm={handleConfirmSubmit}
                              validateBeforeClick={handleValidateBeforeClick}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </Form>
              ) : null}
            </div>
          </>
        ) : null}
      </Box>

      <style jsx>{`
        .info-text {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          margin-bottom: 30px;
        }
        .questions {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }
        .delete {
          margin-top: 25px;
        }
        .submit {
          margin-top: 25px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackSubmission;
